import {useCallback, useEffect, useRef, useState} from 'react';
import {api} from '../../services/api';
import {referenceId} from '../suppliers/SupplierReferences';

const fromRecord = record => ({
    id: record.id, key: record.id, revision: record.revision, itemType: 'supplier', supplierRecordMode: 'reference',
    supplierId: record.supplier_id, name: record.supplier_name || '', description: record.description,
    referenceAmount: record.reference_amount == null ? '' : String(record.reference_amount),
    documentReference: record.document_reference || '', status: record.status,
});
const fields = row => JSON.stringify([row.supplierId, row.description, row.referenceAmount, row.documentReference]);
const dirty = (row, saved) => row.pendingArchive || !row.id || fields(row) !== fields(saved || {});
const validRecord = row => row && typeof row.id === 'string' && typeof row.supplier_id === 'string'
    && Number.isInteger(row.revision) && row.revision > 0 && typeof row.description === 'string'
    && ['active', 'archived'].includes(row.status)
    && (row.reference_amount === null || (row.reference_amount !== '' && Number.isFinite(Number(row.reference_amount)) && Number(row.reference_amount) >= 0));
export const referenceValidation = rows => {
    for (const row of rows.filter(r => r.status === 'active' && !r.pendingArchive)) {
        if (!/^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(row.supplierId || '')) return 'اختر موردًا قائمًا لكل بند توثيق.';
        if (!row.description.trim() || row.description.trim().length > 2000) return 'أدخل وصفًا لكل بند توثيق، بحد أقصى 2000 حرف.';
        if (row.documentReference.length > 200) return 'مرجع المستند يجب ألا يتجاوز 200 حرف.';
        if (row.referenceAmount !== '' && (!/^\d+(\.\d{1,2})?$/.test(row.referenceAmount) || Number(row.referenceAmount) >= 1e12)) return 'أدخل مبلغًا مرجعيًا صحيحًا بمنزلتين عشريتين، أو اتركه فارغًا.';
    }
    return '';
};

export default function useVisitSupplierReferences(visitId, canEdit) {
    const [rows, setRows] = useState([]), [loading, setLoading] = useState(true), [error, setError] = useState('');
    const [saving, setSaving] = useState(false), [saveError, setSaveError] = useState(''), [uncertain, setUncertain] = useState(false);
    const state = useRef([]), saved = useRef(new Map()), attempts = useRef(new Map()), busy = useRef(false), generation = useRef(0);
    const putRows = useCallback(next => {state.current = next; setRows(next);}, []);
    const load = useCallback(async () => {
        if (busy.current) return;
        const current = ++generation.current;
        setLoading(true); setError(''); setSaveError('');
        try {
            const all = [], ids = new Set();
            for (let offset = 0; ; offset += 50) {
                const {data} = await api.get(`/visits/${visitId}/supplier-references`, {params: {offset}});
                if (!Array.isArray(data?.items) || typeof data.has_more !== 'boolean' || !data.items.every(validRecord)) throw new Error('Invalid source');
                for (const row of data.items) {
                    if (ids.has(row.id)) throw new Error('Repeated reference page');
                    ids.add(row.id); all.push(fromRecord(row));
                }
                if (!data.has_more) break;
                if (!data.items.length || offset >= 4950) throw new Error('Incomplete source');
            }
            if (current !== generation.current) return;
            saved.current = new Map(all.map(row => [row.key, row])); attempts.current.clear(); setUncertain(false); putRows(all);
        } catch (e) {
            if (current === generation.current) setError(e?.response?.status === 403 ? 'بنود التوثيق غير متاحة بصلاحياتك الحالية.' : 'بنود التوثيق غير متاحة حاليًا. أعد المحاولة.');
        } finally {if (current === generation.current) setLoading(false);}
    }, [putRows, visitId]);
    useEffect(() => {
        putRows([]); saved.current.clear(); attempts.current.clear(); setUncertain(false); load();
        return () => {generation.current += 1;};
    }, [load, putRows]);
    const canChange = canEdit && !loading && !error && !saving && !uncertain;
    const add = item => {
        if (!canChange || busy.current || item.id) return false;
        try {
            const key = referenceId();
            const total = Number(item.total ?? Number(item.quantity || 1) * Number(item.price || 0));
            putRows([...state.current, {key, itemType: 'supplier', supplierRecordMode: 'reference', supplierId: item.supplierId || '',
                name: item.name || '', description: item.linkedPart || '', referenceAmount: total > 0 ? String(total) : '', documentReference: '', status: 'active'}]);
            return true;
        } catch (_) {setSaveError('تعذر تجهيز بند التوثيق. حدّث المتصفح وحاول مجددًا.'); return false;}
    };
    const update = (key, field, value) => {
        if (!canChange || busy.current || !['supplierId', 'name', 'description', 'referenceAmount', 'documentReference'].includes(field)) return;
        putRows(state.current.map(row => row.key === key && row.status === 'active' ? {...row, [field]: value} : row));
    };
    const remove = key => {
        if (!canChange || busy.current) return;
        putRows(state.current.flatMap(row => row.key !== key ? [row] : row.id ? [{...row, pendingArchive: true}] : []));
    };
    const discardDraft = key => {
        if (!canChange || busy.current) return false;
        const row = state.current.find(r => r.key === key);
        if (!row || row.id) return false;
        putRows(state.current.filter(r => r.key !== key)); return true;
    };
    const pending = rows.some(row => dirty(row, saved.current.get(row.key)));
    const saveAll = async () => {
        if (busy.current) return {ok: false, count: 0};
        const changes = state.current.filter(row => dirty(row, saved.current.get(row.key)));
        if (!changes.length) return {ok: true, count: 0};
        if (!canEdit || loading || error) return {ok: false, count: 0};
        const invalid = referenceValidation(state.current);
        if (invalid) {setSaveError(invalid); return {ok: false, count: 0};}
        busy.current = true; setSaving(true); setSaveError('');
        let count = 0;
        const current = generation.current;
        try {
            for (const row of changes) {
                const record = row.pendingArchive ? {record_mode: 'reference', operation: 'archive'} : {
                    record_mode: 'reference', operation: row.id ? 'update' : 'create', supplier_id: row.supplierId,
                    description: row.description.trim(), reference_amount: row.referenceAmount === '' ? null : row.referenceAmount,
                    document_reference: row.documentReference.trim(),
                };
                const signature = JSON.stringify({record, revision: row.revision || 0});
                let attempt = attempts.current.get(row.key);
                if (!attempt || attempt.signature !== signature) {
                    attempt = {signature, request: referenceId()}; attempts.current.set(row.key, attempt);
                }
                const {data} = await api.put(`/visits/${visitId}/supplier-references/${row.key}`, {
                    request_id: attempt.request, expected_revision: row.revision || 0, record,
                });
                if (current !== generation.current) return {ok: false, count};
                if (!validRecord(data) || data.id !== row.key) throw new Error('Unconfirmed response');
                const next = {...fromRecord(data), name: data.supplier_name || row.name};
                saved.current.set(row.key, next); attempts.current.delete(row.key);
                putRows(state.current.map(r => r.key === row.key ? next : r)); count += 1;
            }
            setUncertain(false); return {ok: true, count};
        } catch (e) {
            if (current !== generation.current) return {ok: false, count};
            const ambiguous = !e?.response?.status || e.response.status >= 500;
            setUncertain(ambiguous);
            setSaveError((count ? `حُفظ ${count} من بنود التوثيق. ` : '') + (ambiguous
                ? 'لم نتأكد من نتيجة بقية الحفظ؛ أعد الضغط على حفظ البنود للتحقق دون تكرار التسجيل.'
                : e.response.status === 409 ? 'تغيّر بند توثيق أو أُغلقت الزيارة. ألغِ التعديل لتحميل البيانات الحالية ثم راجعها.'
                : e.response.status === 403 ? 'لا تملك صلاحية حفظ التوثيق.' : 'تعذر حفظ التوثيق. راجع المورد والوصف والمبلغ.'));
            return {ok: false, count};
        } finally {busy.current = false; if (current === generation.current) setSaving(false);}
    };
    const reset = () => {
        if (busy.current || uncertain) return false;
        putRows([...saved.current.values()]); attempts.current.clear(); setSaveError(''); load(); return true;
    };
    return {rows, loading, error, saving, saveError, uncertain, canChange, pending, add, update, remove, discardDraft, saveAll, reset,
        reload: () => {if (!pending && !uncertain) load();}};
}
